// Shared "Call from Telnyx" opener. Adopts the already-open contact-center
// dialer tab by window name and relays the chat context via postMessage; if
// the dialer doesn't ACK within ackTimeoutMs the fallback depends on what
// kind of window we hold: a blank window we just created is navigated to the
// /dial deep link, while an adopted foreign dialer (stale build or rejected
// message — possibly mid-call) is left completely untouched and the deep
// link opens in a fresh tab instead.

export const DIALER_WINDOW_NAME = 'ydo-telnyx-dialer'

export const buildDialUrl = ({
  base,
  phoneNumber,
  chatId,
  chatType,
  sessionId,
}) => {
  let url =
    `${base}/dial?phoneNumber=${encodeURIComponent(phoneNumber || '')}` +
    `&chatId=${encodeURIComponent(chatId || '')}` +
    `&chatType=${encodeURIComponent(chatType || '')}`
  if (sessionId) {
    url += `&sessionId=${encodeURIComponent(sessionId)}`
  }
  return url
}

export const openTelnyxDialer = (ctx, deps = {}) => {
  const {
    open = (...a) => window.open(...a),
    listen = (fn) => window.addEventListener('message', fn),
    unlisten = (fn) => window.removeEventListener('message', fn),
    schedule = (fn, ms) => setTimeout(fn, ms),
    cancel = (id) => clearTimeout(id),
    ackTimeoutMs = 1500,
  } = deps

  // Adopt-or-create: a blank URL adopts an existing named window without
  // navigating it; if none exists it creates a fresh blank one.
  const win = open('', DIALER_WINDOW_NAME)
  if (!win) {
    // Popup blocked — best effort: try a direct deep-link open.
    open(buildDialUrl(ctx), DIALER_WINDOW_NAME)
    return Promise.resolve('blocked-fallback')
  }

  const expectedOrigin = new URL(ctx.base).origin

  return new Promise((resolve) => {
    let timer = null

    const finish = (result, { focusWin = true } = {}) => {
      unlisten(onMessage)
      cancel(timer)
      if (focusWin) {
        win.focus()
      }
      resolve(result)
    }

    const onMessage = (event) => {
      if (
        event.origin === expectedOrigin &&
        event.source === win &&
        event.data?.type === 'ydo-dial-ack'
      ) {
        finish('relayed')
      }
    }

    listen(onMessage)
    timer = schedule(() => {
      // Only a window we just created (still on about:blank, same-origin
      // readable) may be navigated. If reading location throws, we adopted
      // an existing cross-origin dialer that never ACKed — it could be
      // mid-call, so never touch it.
      let isCreatedBlank = false
      try {
        isCreatedBlank = win.location.href === 'about:blank'
      } catch (_err) {
        isCreatedBlank = false
      }

      if (isCreatedBlank) {
        win.location.href = buildDialUrl(ctx)
        finish('navigated')
      } else {
        open(buildDialUrl(ctx), '_blank')
        finish('fallback-new-tab', { focusWin: false })
      }
    }, ackTimeoutMs)

    win.postMessage(
      {
        type: 'ydo-dial-context',
        phoneNumber: ctx.phoneNumber,
        chatId: ctx.chatId,
        chatType: ctx.chatType,
        sessionId: ctx.sessionId ?? null,
      },
      ctx.base,
    )
  })
}
